import { useEffect, useRef, useState } from "react";
import { formatPhone } from "@/lib/utils";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Bell,
  Check,
  Clock3,
  Crown,
  Eye,
  FileSignature,
  Heart,
  Loader2,
  Mail,
  MessageCircle,
  MessageSquare,
  Pencil,
  Phone,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trophy,
  User,
  UserCheck,
  X,
} from "lucide-react";

import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/toast";
import { ChatSideDrawer } from "@/components/ChatSideDrawer";
import { ChatSenderAvatar, ChatSenderLabel } from "@/components/ChatSenderLabel";
import { Button } from "@/components/ui/button";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  MetricTile,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";
import Loader from "@/components/ui/loader";
import BuyerProfileModal from "@/components/BuyerProfileModal";

const PAGE_SIZE = 21;

// Account statuses where the buyer is locked out of the platform, so staff can
// no longer take action (chat) on them. Mirrors the backend login guard.
const DEACTIVATED_STATUSES = ["inactive"];
const isDeactivated = (status?: string | null) => !!status && DEACTIVATED_STATUSES.includes(status);
const statusLabel = (status?: string | null) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : "";

interface ChatMessage {
  id: string;
  sender_id: string;
  sender_role: string;
  sender_name: string | null;
  sender_avatar_url?: string | null;
  body: string;
  created_at: string;
  edited_at: string | null;
}

function chatDayLabel(iso: string) {
  const d = new Date(iso);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === new Date().toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function ChatModal({ interestId, buyerName, buyerStatus, onClose }: { interestId: string; buyerName: string; buyerStatus?: string | null; onClose: () => void }) {
  const { user } = useAuth();
  const suspended = isDeactivated(buyerStatus);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  // Load/send failures used to be swallowed, so a thread the API refused looked
  // identical to a thread with no messages ("No messages yet") and replies
  // vanished without a word. Always surface the reason instead.
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const fetchMessages = async () => {
    try {
      const msgs = await api.get<ChatMessage[]>(`/interests/${interestId}/messages`);
      setMessages(msgs);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 404
          ? "This conversation could not be loaded for your account. Ask an admin to check that you are the assigned advisor on this listing."
          : e instanceof ApiError && e.message
            ? e.message
            : "Could not load this conversation. Please try again.",
      );
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchMessages(); inputRef.current?.focus(); }, [interestId]); // eslint-disable-line
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      const msg = await api.post<ChatMessage>(`/interests/${interestId}/messages`, { body: body.trim() });
      setMessages((p) => [...p, msg]);
      setBody("");
      setError(null);
    } catch (e) {
      // Keep the draft in the box — the reply is not lost just because it failed.
      setError(
        e instanceof ApiError && e.message
          ? e.message
          : "Your reply could not be sent. Please try again.",
      );
    } finally { setSending(false); }
  };

  const startEdit = (m: ChatMessage) => { setEditingId(m.id); setEditBody(m.body); };
  const cancelEdit = () => { setEditingId(null); setEditBody(""); };

  const saveEdit = async (m: ChatMessage) => {
    const next = editBody.trim();
    if (!next || savingEdit) return;
    if (next === m.body) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      const updated = await api.patch<ChatMessage>(`/interests/${interestId}/messages/${m.id}`, { body: next });
      setMessages((p) => p.map((x) => (x.id === m.id ? updated : x)));
      cancelEdit();
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError && e.message ? e.message : "Could not save your edit. Please try again.",
      );
    } finally { setSavingEdit(false); }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, m: ChatMessage) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(m); }
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  return (
    <ChatSideDrawer
      title={`Chat with ${buyerName}`}
      onClose={onClose}
      badge={suspended && (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-rose-500/90 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-white">
          {statusLabel(buyerStatus)}
        </span>
      )}
    >
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">

          {loading ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : error && messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-center py-12 px-4">
              <X className="h-7 w-7 text-rose-400" />
              <p className="text-sm font-semibold text-rose-700">Conversation unavailable</p>
              <p className="text-xs text-muted-foreground max-w-xs">{error}</p>
              <Button size="sm" variant="outline" className="mt-1 h-7 px-3 text-[11px]" onClick={() => { setLoading(true); fetchMessages(); }}>
                Try again
              </Button>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-center py-12">
              <MessageCircle className="h-7 w-7 text-slate-300" />
              <p className="text-sm text-muted-foreground">No messages yet. Start the conversation.</p>
            </div>
          ) : messages.map((m, i) => {
            const isStaff = m.sender_role === "admin" || m.sender_role === "bl_advisor";
            const mine = user?.id === m.sender_id;
            // Bubbles show only the time, so mark each new day — otherwise Niva's
            // 24h/48h follow-ups (same time of day) read as back-to-back replies.
            const newDay = i === 0 || new Date(messages[i - 1].created_at).toDateString() !== new Date(m.created_at).toDateString();
            return (
              <div key={m.id} className="space-y-3">
              {newDay && (
                <div className="flex items-center gap-2 py-1">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100">
                    {chatDayLabel(m.created_at)}
                  </span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              )}
              <div className={`flex gap-2 ${isStaff ? "flex-row-reverse" : "flex-row"}`}>
                <ChatSenderAvatar role={m.sender_role} name={m.sender_name} src={m.sender_avatar_url} className="mt-0.5" />
                <div className="min-w-0 max-w-[75%] space-y-0.5">
                  <p className={`px-0.5 ${isStaff ? "text-right" : "text-left"}`}>
                    <ChatSenderLabel role={m.sender_role} name={m.sender_name} />
                  </p>
                  {editingId === m.id ? (
                    <div className="flex flex-col gap-1.5 min-w-[220px]">
                      <AutoGrowTextarea
                        autoFocus
                        rows={2}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, m)}
                        className="w-full rounded-xl border border-primary/40 bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={cancelEdit} className="text-[11px] font-semibold text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors">Cancel</button>
                        <Button size="sm" disabled={!editBody.trim() || savingEdit} onClick={() => saveEdit(m)} className="h-7 px-3 text-[11px]">Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div className={`flex items-center gap-1.5 ${isStaff ? "flex-row-reverse" : "flex-row"}`}>
                      <div className={`min-w-0 px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]
                        ${isStaff ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-white border border-border text-foreground rounded-tl-sm shadow-sm"}`}>
                        {m.body}
                      </div>
                      {mine && (
                        <button
                          onClick={() => startEdit(m)}
                          title="Edit message"
                          aria-label="Edit message"
                          className="shrink-0 h-6 w-6 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 flex items-center justify-center opacity-50 hover:opacity-100 transition-all"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                  <p className={`text-[10px] text-muted-foreground ${isStaff ? "text-right" : "text-left"} px-0.5`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {m.edited_at && <span className="italic"> · edited</span>}
                  </p>
                </div>
              </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Send/edit failure on an otherwise-loaded thread */}
        {error && messages.length > 0 && (
          <div className="px-4 py-2 border-t border-rose-200 bg-rose-50 shrink-0">
            <p className="text-xs font-medium text-rose-700">{error}</p>
          </div>
        )}

        {/* Input — replaced by a notice when the buyer's account is deactivated */}
        {suspended ? (
          <div className="px-4 py-3 border-t border-border bg-rose-50 shrink-0">
            <p className="flex items-center justify-center gap-1.5 text-xs font-medium text-rose-700">
              <X className="h-3.5 w-3.5" />
              This buyer is {statusLabel(buyerStatus).toLowerCase()}. No actions can be taken.
            </p>
          </div>
        ) : (
          <div className="px-3 py-3 border-t border-border bg-white shrink-0">
            <div className="flex items-end gap-2 rounded-xl border border-input bg-slate-50/60 px-3 py-2 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all">
              <AutoGrowTextarea
                ref={inputRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Reply to buyer… (Enter to send)"
                rows={1}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground leading-relaxed"
              />
              <button onClick={handleSend} disabled={!body.trim() || sending}
                className="shrink-0 h-8 w-8 rounded-lg bg-gold-gradient text-primary-foreground flex items-center justify-center disabled:opacity-40 hover:brightness-[1.06] transition-[filter]">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        )}
    </ChatSideDrawer>
  );
}

interface InterestItem {
  id: string;
  listing_id: string;
  buyer_id: string;
  message: string | null;
  created_at: string;
  buyer_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  buyer_status: string | null;
  nda_signed: boolean;
  listing_title: string | null;
  listing_reference_code: string | null;
  // Buyer-quality tags. Only sent to admins / BL Advisors, so a seller viewing
  // this same page simply gets no chips.
  buyer_plan_name: string | null;
  buyer_is_premium: boolean;
  buyer_experience: string | null;
  buyer_prequalifications: string[];
  // Set when the buyer took the interest back. The thread is kept for history.
  withdrawn_at: string | null;
  // The listing's assigned BL Advisor; null when none is assigned.
  advisor_id: string | null;
  advisor_name: string | null;
  // Staff only: set once Niva had to follow up because nobody replied in 24h.
  escalation: EscalationInfo | null;
}

interface NivaFollowupStep {
  hours: number;
  due_at: string;
  buyer_message: string;
  staff_title: string;
  staff_body: string;
  staff_recipients: string[];
  alert_status: "sent" | "pending" | "not_sent";
  alert_sent_at: string | null;
  alert_note: string | null;
}

interface EscalationInfo {
  level: number;
  steps: NivaFollowupStep[];
  open: boolean;
  first_reply_at: string | null;
  first_reply_by: string | null;
}

// Counted server-side over everything the viewer can see, not just this page.
interface InterestStats {
  total: number;
  unread: number;
  with_message: number;
  last_24h: number;
  escalated?: number;
  awaiting_reply?: number;
}

type InboxTab = "all" | "escalations";

const hoursBetween = (from: string, to: string) =>
  Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 3_600_000));

const ALERT_STATUS_STYLE: Record<NivaFollowupStep["alert_status"], { label: string; cls: string }> = {
  sent: { label: "Sent", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700" },
  pending: { label: "Pending", cls: "border-amber-500/30 bg-amber-500/10 text-amber-700" },
  not_sent: { label: "Not sent", cls: "border-slate-400/30 bg-slate-400/10 text-slate-600" },
};

/** What Niva told the buyer at 24h / 48h, and the alert she raised to the
 *  BL Advisor (and admins at 48h) at the same moment. */
function EscalationTrail({ item }: { item: InterestItem }) {
  const e = item.escalation!;
  return (
    <div className="mt-3 rounded-[1.2rem] border border-rose-500/25 bg-rose-500/5 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 text-[0.68rem] font-bold uppercase tracking-[0.15em] text-rose-600">
          <AlertTriangle className="h-3.5 w-3.5" />
          Niva escalation trail
        </div>
        {e.open ? (
          <span className="rounded-full bg-rose-500/90 px-2.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-white">
            Awaiting reply · {hoursBetween(item.created_at, new Date().toISOString())}h
          </span>
        ) : e.first_reply_at ? (
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-emerald-700">
            Replied by {e.first_reply_by ?? "staff"} after {hoursBetween(item.created_at, e.first_reply_at)}h
          </span>
        ) : (
          <span className="rounded-full border border-slate-400/30 bg-slate-400/10 px-2.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-slate-600">
            Buyer withdrew
          </span>
        )}
      </div>

      <ol className="mt-3 space-y-3">
        {e.steps.map((s) => {
          const status = ALERT_STATUS_STYLE[s.alert_status];
          return (
            <li key={s.hours} className="rounded-xl border border-border/60 bg-white/80 p-3">
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                {s.hours}h follow-up · {new Date(s.due_at).toLocaleString()}
              </div>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                <div>
                  <div className="inline-flex items-center gap-1 text-[0.7rem] font-semibold text-primary">
                    <Sparkles className="h-3 w-3" /> Niva → Buyer (chat)
                  </div>
                  <p className="mt-0.5 text-xs leading-6 text-foreground">{s.buyer_message}</p>
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 text-[0.7rem] font-semibold text-rose-600">
                      <Bell className="h-3 w-3" /> Niva → {s.staff_recipients.join(" + ")}
                    </span>
                    <span
                      title={s.alert_note ?? undefined}
                      className={`rounded-full border px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide ${status.cls}`}
                    >
                      {status.label}
                      {s.alert_sent_at && ` · ${new Date(s.alert_sent_at).toLocaleString()}`}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs font-semibold leading-6 text-foreground">{s.staff_title}</p>
                  <p className="text-xs leading-5 text-foreground">{s.staff_body}</p>
                  {s.alert_note && <p className="mt-0.5 text-[0.7rem] italic text-muted-foreground">{s.alert_note}</p>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// BuyBox values (users.buyer_preferences) -> the short chip text and the longer
// label behind it. Kept in step with BuyerProfileModal, which spells the same
// values out in full on the buyer profile popup.
const PREQUAL_SHORT: Record<string, string> = {
  sba: "SBA",
  private_lender: "Private Lender",
};

const EXPERIENCE_CHIP: Record<string, { short: string; full: string }> = {
  bought_1_2: { short: "Bought 1–2", full: "Bought 1–2 businesses" },
  bought_3_plus: { short: "Bought 3+", full: "Bought 3+ businesses" },
};

/** The chips that mark a buyer as premium, pre-qualified or experienced. */
function buyerQualityTags(item: InterestItem) {
  // Ordered by PREQUAL_SHORT, not by the stored value: the backend saves the
  // list sorted alphabetically, which would read "Private Lender + SBA".
  const stored = item.buyer_prequalifications ?? [];
  const prequal = [
    ...Object.keys(PREQUAL_SHORT).filter((k) => stored.includes(k)).map((k) => PREQUAL_SHORT[k]),
    ...stored.filter((p) => !(p in PREQUAL_SHORT)),
  ];
  return {
    // Premium = the buyer's org top-priced plan (Operator / ECFA Premium); the
    // chip carries the plan's own name rather than the word "Premium".
    premium: item.buyer_is_premium ? item.buyer_plan_name : null,
    prequalified: prequal.length ? `${prequal.join(" + ")} Pre-Qualified` : null,
    experience: item.buyer_experience ? EXPERIENCE_CHIP[item.buyer_experience] ?? null : null,
  };
}

export default function AdminInterestsPage() {
  const toast = useToast();
  const { hasRole } = useAuth();
  // Only BL's own staff may read a buyer's countersigned NDA — it is a
  // buyer↔BusinessLocating agreement, so the seller view of this same feed
  // keeps "Send NDA" but never gets the signed copy. Mirrors the guard on
  // GET /interests/{id}/nda.pdf.
  const canViewSignedNda = hasRole("admin", "bl_advisor");
  // Escalation detail (Niva's follow-ups and the staff alerts behind them) is
  // internal; the backend only sends it to these roles. Sellers get one feed.
  const isStaff = hasRole("admin", "org_admin", "bl_advisor");
  const isPlatformAdmin = hasRole("admin");
  const [interests, setInterests] = useState<InterestItem[]>([]);
  const [loading, setLoading] = useState(true);
  // Per-interest "Send NDA" progress: interest id → "sending" | "sent".
  const [ndaState, setNdaState] = useState<Record<string, "sending" | "sent">>({});
  // Interest id whose signed NDA is currently being fetched.
  const [ndaOpeningId, setNdaOpeningId] = useState<string | null>(null);

  // The PDF endpoint needs the bearer token, so a plain <a href> can't reach
  // it — fetch as a blob and point a tab at it. The blank tab is opened
  // synchronously (pre-await) so the browser doesn't block it as a popup.
  const handleViewNda = async (item: InterestItem) => {
    if (ndaOpeningId) return;
    setNdaOpeningId(item.id);
    const win = window.open("", "_blank");
    try {
      const blob = await api.blob(`/interests/${item.id}/nda.pdf`);
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      win?.close();
      // requestBlob can't read the JSON detail off a binary endpoint, so spell
      // out the one failure staff will actually hit: the badge says signed but
      // the stamped copy was never built (PDF merge is best-effort at signing).
      toast.error(
        e instanceof ApiError && e.status === 404
          ? "No signed NDA copy is on file for this buyer."
          : "Could not open the signed NDA. Please try again.",
      );
    } finally {
      setNdaOpeningId(null);
    }
  };

  const handleSendNda = async (item: InterestItem) => {
    if (ndaState[item.id]) return; // already sending or sent
    setNdaState((p) => ({ ...p, [item.id]: "sending" }));
    try {
      await api.post(`/interests/${item.id}/send-nda`, {});
      setNdaState((p) => ({ ...p, [item.id]: "sent" }));
      toast.success(`NDA request sent to ${item.buyer_name || "the buyer"}`);
    } catch (e: unknown) {
      setNdaState((p) => {
        const next = { ...p };
        delete next[item.id];
        return next;
      });
      toast.error(e instanceof Error ? e.message : "Could not send the NDA request");
    }
  };
  // Page lives in the URL (?page=N, 1-based) so returning from a listing detail
  // via "Back to listings" restores the exact page the admin was on.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  // Tab lives in the URL too (?tab=escalations), so it survives the same round trip.
  const tab: InboxTab = isStaff && searchParams.get("tab") === "escalations" ? "escalations" : "all";
  const setTab = (next: InboxTab) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "escalations") p.set("tab", next);
        else p.delete("tab");
        p.delete("page");
        return p;
      },
      { replace: true },
    );
  };
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next > 1) p.set("page", String(next));
        else p.delete("page");
        return p;
      },
      { replace: true },
    );
  };
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<InterestStats | null>(null);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);
  // Mirrors lastSeenAt for fetches fired before the state update has rendered.
  const sinceRef = useRef<string | null>(null);
  const [openChatId, setOpenChatId] = useState<string | null>(null);
  const [profileBuyerId, setProfileBuyerId] = useState<string | null>(null);
  // `searchInput` is the raw field value; `search` is the committed query that
  // actually triggers a server fetch (on Enter / blur), matching Buyer Activity.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);

  function fetchInterests(targetPage = page) {
    setLoading(true);
    const params = new URLSearchParams({ page: String(targetPage), page_size: String(PAGE_SIZE) });
    if (search.trim()) params.set("search", search.trim());
    if (sinceRef.current) params.set("since", sinceRef.current);
    if (tab === "escalations") params.set("escalated", "true");
    api
      .get<{ total_elements: number; stats: InterestStats; data: InterestItem[] }>(`/interests/all?${params.toString()}`)
      .then((res) => { setInterests(res.data); setTotal(res.total_elements); setStats(res.stats); setPage(targetPage); })
      .catch(() => {})
      .finally(() => { setLoading(false); setInitialLoad(false); });
  }

  useEffect(() => {
    api
      .patch<{ ok: boolean; previous_last_seen_at: string | null }>("/interests/mark-seen", {})
      .then((res) => { sinceRef.current = res.previous_last_seen_at; setLastSeenAt(res.previous_last_seen_at); })
      .catch(() => {})
      .finally(() => fetchInterests(page));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-query from page 1 whenever the committed search term or the tab
  // changes. The mount fetch above handles the initial load, so this skips its
  // first run.
  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current) { didInit.current = true; return; }
    fetchInterests(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tab]);

  const isNew = (createdAt: string) => {
    if (!lastSeenAt) return true;
    return new Date(createdAt) > new Date(lastSeenAt);
  };

  // From the server's stats: counting the cards on screen capped every KPI at
  // the page size, and the total moved with the search box.
  const metrics = {
    total: stats?.total ?? total,
    unread: stats?.unread ?? 0,
    withMessage: stats?.with_message ?? 0,
    last24h: stats?.last_24h ?? 0,
    escalated: stats?.escalated ?? 0,
    awaitingReply: stats?.awaiting_reply ?? 0,
  };

  // Passed as router state on each listing link so ListingDetail's
  // "Back to listings" returns to this exact page. Derived from the current
  // pathname because this component is mounted under several role prefixes
  // (/admin/interests, /seller/interests, /bl-advisor/interests).
  const { pathname } = useLocation();
  const backTo = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
  // Same reason: each prefix has its own home (/admin/home, /bl-advisor/home…).
  const homePath = pathname.replace(/interests\/?$/, "home");

  if (initialLoad) {
    return <Loader className="min-h-[60vh]" />;
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Interest Inbox"
        title="Review inbound buyer intent with less noise and better context."
        description="The interest feed is now structured to help admins and BL Advisors scan new demand quickly, prioritize follow-up, and jump into listing detail with full buyer context."
        actions={
          <>
            <Button size="lg" className="rounded-full" onClick={() => window.location.reload()}>
              Refresh inbox
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Link to={homePath}>
              <Button size="lg" variant="outline" className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white">
                Back to dashboard
              </Button>
            </Link>
          </>
        }
        aside={
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">Demand signals</div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">{metrics.total} buyer interests</div>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              {metrics.unread} new since your last visit and {metrics.last24h} received in the last 24 hours.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">New</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.unread}</div>
              </div>
              {isStaff ? (
                <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                  <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Awaiting reply</div>
                  <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.awaitingReply}</div>
                </div>
              ) : (
                <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                  <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Messages</div>
                  <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.withMessage}</div>
                </div>
              )}
            </div>
          </>
        }
      />

      <section className={`grid gap-4 sm:grid-cols-2 ${isStaff ? "lg:grid-cols-3 xl:grid-cols-5" : "xl:grid-cols-4"}`}>
        <MetricTile label="Total Interests" value={metrics.total} icon={Heart} tone="rose" />
        <MetricTile label="Unread" value={metrics.unread} icon={Sparkles} tone="primary" />
        <MetricTile label="With Message" value={metrics.withMessage} icon={MessageSquare} tone="amber" />
        <MetricTile label="Last 24 Hours" value={metrics.last24h} icon={Clock3} tone="emerald" />
        {isStaff && (
          <MetricTile label="Escalated" value={metrics.escalated} icon={AlertTriangle} tone="rose" />
        )}
      </section>

      <PagePanel>
        <PanelHeading
          title={tab === "escalations" ? "Escalations" : "Buyer interest stream"}
          description={
            tab === "escalations"
              ? "Interests nobody answered within 24 hours. Each one shows what Niva told the buyer and the alert she sent to the BL Advisor (and to all admins at 48 hours)."
              : "Each card includes listing link, contact details, and optional buyer message for immediate follow-up."
          }
        />

        {isStaff && (
          <div role="tablist" className="mb-4 flex gap-6 border-b border-border">
            {([
              { key: "all", label: "Interests", count: metrics.total },
              { key: "escalations", label: "Escalations", count: metrics.escalated },
            ] as const).map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`-mb-px flex cursor-pointer items-center gap-2 border-b-2 pb-2.5 text-sm transition-colors ${
                  tab === t.key
                    ? "border-primary font-semibold text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
                <span
                  className={`rounded-md px-1.5 py-0.5 text-xs tabular-nums ${
                    t.key === "escalations" && metrics.awaitingReply > 0
                      ? "bg-rose-500/15 text-rose-600"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput); }}
              onBlur={() => setSearch(searchInput)}
              placeholder="Search by buyer name, email, phone, listing, or message…"
              className="w-full rounded-xl border border-input bg-white py-2 pl-9 pr-9 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => { setSearchInput(""); setSearch(""); }}
                title="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <Loader className="min-h-[40vh]" />
        ) : interests.length === 0 ? (
          <EmptyPanel
            icon={Heart}
            title={
              search.trim()
                ? "No interests match your search"
                : tab === "escalations"
                  ? "No escalations"
                  : "No interests received yet"
            }
            description={
              search.trim()
                ? "Try a different buyer name, email, phone, listing, or message."
                : tab === "escalations"
                  ? "Every buyer has had a reply within 24 hours, so Niva hasn't had to follow up."
                  : "Once buyers begin submitting intent, their details and messages will appear here."
            }
          />
        ) : (
          <div className="space-y-3">
            {interests.map((item) => {
              const itemIsNew = isNew(item.created_at);
              const quality = buyerQualityTags(item);
              return (
                <article
                  key={item.id}
                  className={`rounded-[1.55rem] border p-4 shadow-[0_1px_0_hsl(0_0%_100%/0.82)_inset] transition-all duration-200 ${
                    itemIsNew
                      ? "border-primary/35 bg-primary/5"
                      : "border-border/60 bg-white/72 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-white"
                  }`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                    <button
                      type="button"
                      onClick={() => setProfileBuyerId(item.buyer_id)}
                      title="View buyer profile"
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[1rem] bg-primary/10 text-primary transition-transform hover:scale-105 hover:ring-2 hover:ring-primary/30"
                    >
                      <User className="h-5 w-5" />
                    </button>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setProfileBuyerId(item.buyer_id)}
                          className="text-sm font-black text-foreground hover:text-primary hover:underline underline-offset-2 transition-colors"
                          title="View buyer profile"
                        >
                          {item.buyer_name || "Unknown buyer"}
                        </button>
                        {itemIsNew && (
                          <span className="badge-new-flash rounded-full bg-gold-gradient px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-primary-foreground">
                            New
                          </span>
                        )}
                        {quality.premium && (
                          <span
                            title={`${quality.premium} plan — the top tier this buyer's organization sells`}
                            className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-primary"
                          >
                            <Crown className="h-3 w-3" />
                            {quality.premium}
                          </span>
                        )}
                        {quality.prequalified && (
                          <span
                            title="Pre-qualified with a lender, per the buyer's Buy Box"
                            className="inline-flex items-center gap-1 rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-sky-600"
                          >
                            <BadgeCheck className="h-3 w-3" />
                            {quality.prequalified}
                          </span>
                        )}
                        {quality.experience && (
                          <span
                            title={`${quality.experience.full}, per the buyer's Buy Box`}
                            className="inline-flex items-center gap-1 rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-violet-600"
                          >
                            <Trophy className="h-3 w-3" />
                            {quality.experience.short}
                          </span>
                        )}
                        {item.withdrawn_at && (
                          <span
                            title={`Buyer withdrew on ${new Date(item.withdrawn_at).toLocaleDateString()}`}
                            className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-rose-500"
                          >
                            Withdrawn
                          </span>
                        )}
                        {isDeactivated(item.buyer_status) && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-rose-700">
                            {statusLabel(item.buyer_status)}
                          </span>
                        )}
                        {item.nda_signed && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-emerald-600">
                            <ShieldCheck className="h-3 w-3" />
                            NDA Signed
                          </span>
                        )}
                        {item.escalation && tab === "all" && (
                          <button
                            type="button"
                            onClick={() => setTab("escalations")}
                            title={
                              item.escalation.open
                                ? `No staff reply yet. Niva has sent her ${item.escalation.level}h follow-up.`
                                : `Niva sent a ${item.escalation.level}h follow-up before anyone replied.`
                            }
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] ${
                              item.escalation.open
                                ? "border-rose-500/30 bg-rose-500/10 text-rose-600"
                                : "border-slate-400/30 bg-slate-400/10 text-slate-600"
                            }`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {item.escalation.level >= 48 ? "Escalated 48h" : "Late 24h"}
                            {!item.escalation.open && " · Answered"}
                          </button>
                        )}
                      </div>

                      <Link
                        to={`/listings/${item.listing_id}`}
                        state={{ from: backTo }}
                        className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-light"
                      >
                        {item.listing_title || "View listing"}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                      {item.listing_reference_code && (
                        <span className="ml-2 font-mono text-[0.7rem] text-muted-foreground">{item.listing_reference_code}</span>
                      )}

                      <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span>{new Date(item.created_at).toLocaleString()}</span>
                        {item.buyer_email && (
                          <span className="inline-flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5" />
                            {item.buyer_email}
                          </span>
                        )}
                        {item.buyer_phone && (
                          <span className="inline-flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5" />
                            {formatPhone(item.buyer_phone)}
                          </span>
                        )}
                        {item.advisor_name ? (
                          <span className="inline-flex items-center gap-1.5 font-semibold text-foreground/80" title="BL Advisor assigned to this listing">
                            <UserCheck className="h-3.5 w-3.5 text-primary" />
                            BL Advisor: {item.advisor_name}
                          </span>
                        ) : isPlatformAdmin && (
                          <span className="inline-flex items-center gap-1.5 font-semibold text-amber-700" title="Alerts for this listing go to all admins">
                            <UserCheck className="h-3.5 w-3.5" />
                            No BL Advisor assigned
                          </span>
                        )}
                      </div>

                      {item.message && (
                        <div className="mt-3 rounded-[1.2rem] border border-border/60 bg-white/70 p-3">
                          <div className="inline-flex items-center gap-1.5 text-[0.68rem] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                            <MessageSquare className="h-3.5 w-3.5" />
                            Initial message
                          </div>
                          <p className="mt-1 text-sm leading-7 text-foreground">{item.message}</p>
                        </div>
                      )}

                      {tab === "escalations" && item.escalation && <EscalationTrail item={item} />}

                      {/* Actions: chat + (for unsigned buyers) send NDA, or
                          (for staff, once signed) read the signed copy */}
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => setOpenChatId(item.id)}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors bg-white border-primary/20 text-primary hover:bg-primary/5"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                          Open Chat
                        </button>
                        {item.nda_signed && canViewSignedNda && (
                          <button
                            onClick={() => handleViewNda(item)}
                            disabled={ndaOpeningId === item.id}
                            title="Open the buyer's signed NDA (opens in a new tab)"
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors bg-white border-emerald-500/30 text-emerald-700 hover:bg-emerald-500/10 disabled:opacity-60"
                          >
                            {ndaOpeningId === item.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                            View Signed NDA
                          </button>
                        )}
                        {!item.nda_signed && (
                          ndaState[item.id] === "sent" ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-600">
                              <Check className="h-3.5 w-3.5" />
                              NDA Sent
                            </span>
                          ) : (
                            <button
                              onClick={() => handleSendNda(item)}
                              disabled={ndaState[item.id] === "sending"}
                              title="Email the buyer a request to sign the NDA"
                              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors bg-white border-amber-500/30 text-amber-700 hover:bg-amber-500/10 disabled:opacity-60"
                            >
                              {ndaState[item.id] === "sending" ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <FileSignature className="h-3.5 w-3.5" />
                              )}
                              Send NDA
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <Pagination
          page={page}
          totalElements={total}
          pageSize={PAGE_SIZE}
          onChange={fetchInterests}
          className="mt-6"
        />
      </PagePanel>

      {/* Buyer profile popup */}
      {profileBuyerId && (
        <BuyerProfileModal
          buyerId={profileBuyerId}
          onClose={() => setProfileBuyerId(null)}
          // One buyer can hold several interests on this page; step through
          // each buyer once, in the order they first appear.
          buyerIds={[...new Set(interests.map((i) => i.buyer_id))]}
          onNavigate={setProfileBuyerId}
        />
      )}

      {/* Chat popup modal */}
      {openChatId && (() => {
        const item = interests.find((i) => i.id === openChatId);
        return item ? (
          <ChatModal
            interestId={item.id}
            buyerName={item.buyer_name ?? "Buyer"}
            buyerStatus={item.buyer_status}
            onClose={() => setOpenChatId(null)}
          />
        ) : null;
      })()}
    </PageShell>
  );
}
